import React from "react";
import { BarChart3, GitBranch, Database, LineChart, ArrowUpRight } from "lucide-react";

const capabilities = [
  {
    icon: GitBranch,
    title: "SAP S/4HANA EHS — Workplace Safety",
    body:
      "Greenfield implementation and stabilization support for the Workplace Safety module — BGM (Basic Data & Tools), SVT (Specification Management), GLM (Global Label Management) and IDoc coordination.",
  },
  {
    icon: Database,
    title: "Process Mapping & Master Data",
    body:
      "From incident workflows to hazardous substance master data — every business process mapped, every master record defensible during audit.",
  },
  {
    icon: BarChart3,
    title: "Power BI EHS Dashboards",
    body:
      "Leading & lagging indicator dashboards for executives: LTIFR, TRIR, near-miss trends, observation closure, audit findings, training compliance — refreshed automatically.",
  },
  {
    icon: LineChart,
    title: "KPI Frameworks & Reporting",
    body:
      "Management review packs, action trackers, and monthly EHS scorecards designed to drive accountability, not just produce slides.",
  },
];

const kpis = [
  { label: "LTIFR", desc: "Lost Time Injury Frequency Rate" },
  { label: "TRIR", desc: "Total Recordable Incident Rate" },
  { label: "Near-Miss Index", desc: "Leading indicator culture metric" },
  { label: "CAPA Closure %", desc: "Corrective action discipline" },
  { label: "Training Coverage", desc: "Competency vs requirement" },
  { label: "Audit Findings", desc: "Severity, ageing, ownership" },
];

export default function SapDigital() {
  return (
    <section
      data-testid="sap-digital"
      id="sap-digital"
      className="dark-section bg-bgdark text-white border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <p className="mono-label">Digital EHS — SAP &amp; Power BI</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mt-5 leading-[0.95]">
              When spreadsheets
              <br />
              <span className="text-accent-amber">stop scaling</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-white/70 leading-relaxed">
              Most organisations have safety data — they just can&apos;t trust
              it. We design the digital backbone — SAP S/4HANA EHS for the
              system of record, Power BI for the system of insight — so your
              leadership sees the truth in real time.
            </p>
          </div>
        </div>

        {/* Capability grid */}
        <div
          data-testid="sap-capabilities"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10"
        >
          {capabilities.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`sap-cap-${i}`}
                className="border-r border-b border-white/10 p-7 lg:p-8 min-h-[280px] flex flex-col group hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[11px] text-white/40">
                    Cap 0{i + 1}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-white/30 group-hover:text-accent-amber transition-colors"
                  />
                </div>
                <Icon
                  size={26}
                  strokeWidth={1.4}
                  className="text-accent-amber mt-6"
                />
                <h3 className="font-display text-lg uppercase tracking-tight mt-5 leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  {c.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* KPI ticker card */}
        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4">
            <p className="mono-label">Metrics we operationalise</p>
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-4 leading-tight">
              The numbers that
              <br />
              actually move
              <br />
              the needle.
            </h3>
            <p className="text-white/65 text-sm mt-5 leading-relaxed">
              Our dashboards focus on leading indicators — the ones that
              predict incidents before they happen — not vanity reporting.
            </p>
          </div>

          <div
            data-testid="sap-kpi-grid"
            className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 border-t border-l border-white/10"
          >
            {kpis.map((k, i) => (
              <div
                key={k.label}
                data-testid={`sap-kpi-${i}`}
                className="border-r border-b border-white/10 px-5 py-6 hover:bg-white/[0.04] transition-colors"
              >
                <div className="font-display text-2xl text-accent-amber tracking-tight">
                  {k.label}
                </div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-white/55 mt-2 leading-relaxed">
                  {k.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
